/**
 * Revisar un apunte, o escribir uno a mano.
 *
 * EL MISMO PANEL PARA LAS DOS COSAS, y no por ahorrar código: lo que se hace es idéntico
 * —concepto, importe y fecha—, y lo único que cambia es de dónde vienen los valores. Dos
 * paneles distintos habrían obligado a mantener dos veces la misma validación.
 *
 * CONFIRMAR ES EL BOTÓN PRINCIPAL cuando el apunte lo rellenó la aplicación: es la diferencia
 * entre «he corregido el importe» y «esto ya está bien». Borrar el apunte NO está aquí: va en el
 * menú «⋯» de su fila en la tabla de conciliación, con confirmación, lejos de «Guardar».
 *
 * EL PISO Y EL PERIODO FACTURADO se corrigen aquí también. De los dos depende, en Suministros,
 * qué inquilinos pagan una factura: los del piso, por los días que vivían en él dentro del
 * periodo. Una luz en el piso equivocado o con el periodo mal leído se la cobra a otros.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LedgerEntry } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import { Field, FormRow, FormSection, Input, MoneyInput, Select, Textarea } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { avisarReapertura } from './MissingPanel';
import { aPropertyId } from './reconciliation';
import styles from './Ledger.module.css';

interface EntryEditDrawerProps {
  open: boolean;
  month: string;
  onClose: () => void;
  propertyName: string;
  /** Presente al revisar un apunte que ya existe. */
  entry?: LedgerEntry;
  /** Presente al apuntar uno nuevo a mano en un inmueble concreto. */
  propertyId?: string;
  /**
   * Los inmuebles entre los que elegir: al apuntar uno nuevo SIN inmueble elegido (desde la tabla
   * de conciliación), o para MOVER a otro piso uno que ya existe.
   */
  properties?: { id: string; name: string }[];
  /** El piso del apunte que se revisa, para poder cambiarlo. */
  currentPropertyId?: string;
}

function primerDiaDe(month: string): string {
  return `${month}-01`;
}

export function EntryEditDrawer({
  open,
  month,
  onClose,
  propertyName,
  entry,
  propertyId,
  properties,
  currentPropertyId,
}: EntryEditDrawerProps) {
  const queryClient = useQueryClient();
  const editing = Boolean(entry);

  const [concept, setConcept] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [inmueble, setInmueble] = useState('');
  const elegirInmueble = !entry && !propertyId && Boolean(properties?.length);
  /** Al revisar uno que existe, el piso se puede cambiar si hay entre cuáles elegir. */
  const cambiarInmueble = Boolean(entry && currentPropertyId && properties && properties.length > 1);
  const [amount, setAmount] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [serviceFrom, setServiceFrom] = useState('');
  const [serviceTo, setServiceTo] = useState('');
  const [notes, setNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setConcept(entry?.concept ?? '');
    setVendorName(entry?.vendorName ?? '');
    setInmueble(entry ? (currentPropertyId ?? '') : '');
    setAmount(entry?.amount ?? '');
    setIssuedAt(entry?.issuedAt ?? primerDiaDe(month));
    setServiceFrom(entry?.servicePeriod?.from ?? '');
    setServiceTo(entry?.servicePeriod?.to ?? '');
    setNotes(entry?.notes ?? '');
    setFieldErrors({});
  }, [open, entry, month, currentPropertyId]);

  function onError(error: unknown) {
    if (!(error instanceof ApiError)) {
      toast.error('No se ha podido guardar el apunte.');
      return;
    }
    const errors: Record<string, string> = {};
    for (const detail of error.fieldErrors) errors[detail.path] = detail.message;
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) toast.error(error.message);
  }

  const save = useMutation({
    mutationFn: async (confirmar: boolean): Promise<{ reopened: boolean }> => {
      const body = {
        concept: concept.trim(),
        vendorName: vendorName.trim() || null,
        amount,
        issuedAt,
        serviceFrom: serviceFrom || null,
        serviceTo: serviceTo || null,
        notes: notes.trim() || null,
        ...(confirmar ? { status: 'confirmado' as const } : {}),
      };
      if (entry) {
        await api.updateLedgerEntry(entry.id, {
          ...body,
          ...(cambiarInmueble && inmueble && inmueble !== currentPropertyId ? { propertyId: aPropertyId(inmueble) } : {}),
        });
        return { reopened: false };
      }
      // «Gastos generales» se manda como «sin inmueble».
      return api.createLedgerEntry(month, { ...body, propertyId: aPropertyId(propertyId ?? inmueble) });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(editing ? 'Apunte guardado.' : 'Apunte añadido.');
      if (result.reopened) avisarReapertura(month);
      onClose();
    },
    onError,
  });

  // El periodo, los dos extremos o ninguno; el orden lo comprueba la API.
  const periodoCompleto = Boolean(serviceFrom) === Boolean(serviceTo);
  const valido = Boolean(concept.trim() && amount && issuedAt && (!elegirInmueble || inmueble) && periodoCompleto);
  const sinRevisar = entry?.status === 'borrador';

  return (
    <Drawer
      open={open}
      title={editing ? 'Revisar el apunte' : 'Apuntar un gasto'}
      subtitle={propertyName}
      onClose={onClose}
      dirty={valido}
      footer={
        <div className={styles.pie}>
          {/* El hueco mantiene a «Cancelar» y «Guardar» a la derecha. */}
          <span />
          <div className={styles.pieDerecha}>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={() => save.mutate(true)} disabled={!valido || save.isPending}>
              {save.isPending ? 'Guardando…' : sinRevisar ? 'Confirmar' : 'Guardar'}
            </Button>
          </div>
        </div>
      }
    >
      {sinRevisar && entry?.guessedFrom && (
        <FormSection title="Sin revisar" hint={entry.guessedFrom}>
          <p className={styles.aviso}>
            El concepto y el importe los ha rellenado la aplicación a partir del fichero.
            Compáralos con la factura: hasta que lo confirmes, este gasto cuenta como pendiente
            en la conciliación del mes.
          </p>
        </FormSection>
      )}

      <FormSection title="El gasto">
        {cambiarInmueble && (
          <Field
            label="Inmueble"
            help="De qué piso es. En Suministros, una factura la pagan los inquilinos de su piso."
            error={fieldErrors.propertyId}
          >
            <Select
              options={(properties ?? []).map((row) => ({ value: row.id, label: row.name }))}
              value={inmueble}
              onChange={(event) => setInmueble(event.target.value)}
            />
          </Field>
        )}
        {elegirInmueble && (
          <Field label="Inmueble" required error={fieldErrors.propertyId}>
            <Select
              options={(properties ?? []).map((row) => ({ value: row.id, label: row.name }))}
              placeholder="Elige el inmueble…"
              value={inmueble}
              onChange={(event) => setInmueble(event.target.value)}
            />
          </Field>
        )}
        <Field label="Concepto" required error={fieldErrors.concept}>
          <Input
            value={concept}
            onChange={(event) => setConcept(event.target.value)}
            placeholder="Comunidad"
            maxLength={160}
            invalid={Boolean(fieldErrors.concept)}
            autoFocus
          />
        </Field>

        <Field
          label="Proveedor"
          optional
          help="Quién emite la factura. Al subir un ticket lo lee de su cabecera."
          error={fieldErrors.vendorName}
        >
          <Input
            value={vendorName}
            onChange={(event) => setVendorName(event.target.value)}
            placeholder="Iberdrola"
            maxLength={120}
            invalid={Boolean(fieldErrors.vendorName)}
          />
        </Field>

        <FormRow>
          <Field label="Importe" required error={fieldErrors.amount}>
            <MoneyInput
              value={amount}
              onValueChange={setAmount}
              onChange={() => undefined}
              invalid={Boolean(fieldErrors.amount)}
            />
          </Field>
          <Field
            label="Fecha"
            required
            help="Tiene que caer dentro del mes que estás cuadrando."
            error={fieldErrors.issuedAt}
          >
            <Input
              type="date"
              value={issuedAt}
              onChange={(event) => setIssuedAt(event.target.value)}
              invalid={Boolean(fieldErrors.issuedAt)}
            />
          </Field>
        </FormRow>

        <FormRow>
          <Field
            label="Periodo facturado, desde"
            optional
            help="El que cobra la factura de luz, agua o wifi. Vacío: el mes del apunte."
            error={fieldErrors.serviceFrom}
          >
            <Input
              type="date"
              value={serviceFrom}
              onChange={(event) => setServiceFrom(event.target.value)}
              invalid={Boolean(fieldErrors.serviceFrom) || !periodoCompleto}
            />
          </Field>
          <Field
            label="Hasta"
            optional
            help={periodoCompleto ? 'Se reparte entre quienes vivían en el piso esos días.' : 'Pon las dos fechas, o ninguna.'}
            error={fieldErrors.serviceTo}
          >
            <Input
              type="date"
              value={serviceTo}
              min={serviceFrom || undefined}
              onChange={(event) => setServiceTo(event.target.value)}
              invalid={Boolean(fieldErrors.serviceTo) || !periodoCompleto}
            />
          </Field>
        </FormRow>

        <Field label="Notas" optional>
          <Textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
            maxLength={1000}
          />
        </Field>
      </FormSection>
    </Drawer>
  );
}
